import { HandleError } from "../utils";
import { UserServices } from "./UserServices";

const API_URL = (() => {
	// Em desenvolvimento, usar a URL local se disponível
	if (process.env.NODE_ENV === "development" && process.env.REACT_APP_API_LOCAL_URL) {
		return process.env.REACT_APP_API_LOCAL_URL;
	}
	
	const url = process.env.REACT_APP_API_URL || "/api";
	return url.startsWith("http://") || url.startsWith("https://") ? url : `${window.location.origin}${url}`;
})();

const tryResponse = async (response: Response) => {
	const contentType = response.headers.get("Content-Type") ?? "";

	if (response.status === 401 && UserServices.currentUser) {
		UserServices.logout();
		// window.location.reload();
	}

	if (HandleError.errorStatus.includes(response.status)) {
		if (contentType.includes("application/json")) {
			const { message, name, code } = await response.json();
			throw new HandleError(message, name, code);
		} else {
			throw new HandleError(await response.text(), "INTERNAL_ERROR", response.status as any);
		}
	}
};

type RequestBody = Record<PropertyKey, any> | FormData;

interface DataFile {
	data: File | ArrayBuffer | Blob | string | BlobPart[];
	fileName: string;
	lastModified?: number;
	endings?: EndingType;
	type?: string;
}

type Params = Record<PropertyKey, string | boolean | number | null | undefined>;

/**
 * Resolve um caminho relativo em relação a uma URL base
 * @param {string} base - URL base
 * @param {string} path - Caminho relativo
 * @returns {string} URL absoluta resolvida
 */
function resolveUrl(base: string, path: string, ...paths: string[]): string {
	const resolved = (() => {
		// Se o path já for uma URL completa, retorna direto
		try {
			new URL(path);
			return path;
		} catch {}

		// Cria objeto URL da base
		const baseUrl = new URL(base);

		// Resolve caminhos absolutos (começam com /)
		if (path.startsWith("/")) {
			baseUrl.pathname = path;
			return baseUrl.toString();
		}

		// Remove ./ do início se existir
		const cleanPath = path.startsWith("./") ? path.substring(2) : path;

		// Para caminhos com ../, usa o construtor URL com base
		if (cleanPath.startsWith("../") || cleanPath.includes("/../")) {
			// Combina caminho atual da base com o novo caminho
			const currentPath = baseUrl.pathname.endsWith("/") ? baseUrl.pathname : baseUrl.pathname + "/";
			const fullPath = currentPath + cleanPath;

			// Normaliza o caminho (remove ../ e ./)
			const segments = fullPath.split("/").filter(Boolean);
			const result = [];

			for (const segment of segments) {
				if (segment === "..") {
					result.pop();
				} else if (segment !== ".") {
					result.push(segment);
				}
			}

			baseUrl.pathname = "/" + result.join("/");
			return baseUrl.toString();
		}

		// Para caminhos relativos simples
		const basePath = baseUrl.pathname;
		const finalPath = basePath.endsWith("/") ? basePath + cleanPath : basePath + "/" + cleanPath;

		baseUrl.pathname = finalPath;
		return baseUrl.toString();
	})();

	paths = paths.filter((s) => typeof s === "string" && s.trim() !== "");

	return paths.length ? resolveUrl.apply(null, [resolved, ...paths] as [string, string, ...string[]]) : resolved;
}

export class APIClientServices {
	static API_URL = API_URL;

	static async __execute__<R = any>(
		url: string,
		body?: RequestBody,
		method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "OPTIONS" = "GET",
	): Promise<R> {
		const headers = new Headers();
		if (UserServices.currentToken) headers.set("Authorization", `Bearer ${UserServices.currentToken}`);
		if (body && !(body instanceof FormData)) headers.set("Content-Type", "application/json");
		headers.set("X-Requested-From", window.location.href);

		const response = await fetch(url, {
			method,
			headers,
			body: body ? (body instanceof FormData ? body : JSON.stringify(body ?? {})) : undefined,
		});

		await tryResponse(response);

		const contentType = response.headers.get("Content-Type") ?? "";

		if (contentType.includes("application/json")) {
			return response.json();
		} else if (contentType.includes("text/html")) {
			return response.text() as any;
		}

		return response.blob() as any;
	}

	static __isBase64__(str: any): str is string {
		return (
			typeof str === "string" &&
			/^(data:([a-z]+\/[a-z0-9-+.]+(;[a-z-]+=[a-z0-9-]+)?)?(;base64)?,)?(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/gi.test(
				str,
			)
		);
	}

	static async __base64ToFile__(dataurl: string, filename: string = "file", options: FilePropertyBag = {}): Promise<File> {
		const [metadata] = dataurl.split(",");
		options.type = options?.type ?? metadata.match(/:(.*?);/)?.[1] ?? "application/octet-stream";
		if (!dataurl.startsWith("data:")) {
			dataurl = `data:${options.type};base64,` + dataurl;
		}
		return await fetch(dataurl)
			.then((res) => res.arrayBuffer())
			.then((data) => {
				const blob = new Blob([data], { type: options.type });
				return Promise.resolve(new File([blob], filename, options));
			});
	}

	static __getParams__(params: Params, context?: URLSearchParams): string {
		const p = Object.entries(params).reduce(
			(acc, [key, value]) => {
				if (value !== undefined && value !== null) {
					acc[key] = value.toString();
				}
				return acc;
			},
			{} as Record<PropertyKey, string>,
		);

		if (context) {
			Object.entries(p).forEach(([key, value]) => {
				context.append(key, value);
			});
		}

		return Object.entries(p)
			.map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
			.join("&");
	}

	/**
	 * @description Prepare a FormData object from a record of data.
	 *
	 * @example
	 * ```ts
	 * const formData = await APIController.formData({
	 *      name: "John Doe",
	 *      age: 30,
	 *      file: new File(["Hello World"], "hello.txt", { type: "text/plain" }),
	 *  });
	 *
	 *  // formData.get("name") === "John Doe"
	 *  // formData.get("age") === "30"
	 *  // formData.get("file") === File
	 * ```
	 */
	static async formData(data: Record<PropertyKey, File | ArrayBuffer | Blob | string | BlobPart[] | DataFile>): Promise<FormData> {
		const formData = new FormData();
		for (const key in data) {
			const {
				data: dataFile,
				fileName,
				...options
			}: DataFile = typeof data[key] === "object" && "data" in (data as any)[key] && "fileName" in (data as any)[key]
				? (data as any)[key]
				: ({
						data: data[key],
						fileName: "file",
					} as any);

			if (dataFile instanceof File) {
				formData.append(key, dataFile);
			} else if (dataFile instanceof Blob) {
				formData.append(key, new File([dataFile], "file", { ...options, type: options.type ?? dataFile.type }));
			} else if (dataFile instanceof ArrayBuffer) {
				formData.append(key, new File([dataFile], "file", options));
			} else if (this.__isBase64__(dataFile)) {
				formData.append(key, await this.__base64ToFile__(dataFile, fileName, options));
			}
		}
		return formData;
	}

	static async encryptData(data: string): Promise<string> {
		const pem = await this.get<string>("/get-public-key");

		async function pemToArrayBuffer() {
			// Remove cabeçalhos e quebras de linha
			const base64 = pem.replace(/-----(BEGIN|END) PUBLIC KEY-----/g, "").replace(/\s+/g, "");
			const binary = atob(base64);
			const bytes = new Uint8Array(binary.length);
			for (let i = 0; i < binary.length; i++) {
				bytes[i] = binary.charCodeAt(i);
			}
			return bytes.buffer;
		}

		const spki = await pemToArrayBuffer();
		const cryptoKey = await window.crypto.subtle.importKey(
			"spki", // formato da chave pública
			spki, // ArrayBuffer
			{
				name: "RSA-OAEP",
				hash: "SHA-256", // deve ser igual ao da chave
			},
			false, // extractable (false = não pode exportar)
			["encrypt"], // operações permitidas
		);

		const encoded = new TextEncoder().encode(data);
		const encrypted = await window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, cryptoKey, encoded);
		// Converter para Base64 (útil para transmitir)
		return JSON.stringify({ encrypted: btoa(String.fromCharCode(...new Uint8Array(encrypted))) });
	}

	static async get<R = any>(route: string, params?: Params): Promise<R> {
		const url = new URL(resolveUrl(API_URL, route));
		if (params) {
			this.__getParams__(params, url.searchParams);
		}
		return await this.__execute__<R>(url.toString());
	}

	static async post<R = any>(route: string, body?: RequestBody, params?: Params): Promise<R> {
		const url = new URL(resolveUrl(API_URL, route));
		if (params) {
			this.__getParams__(params, url.searchParams);
		}
		return await this.__execute__<R>(url.toString(), body, "POST");
	}

	static async put<R = any>(route: string, body?: RequestBody, params?: Params): Promise<R> {
		const url = new URL(resolveUrl(API_URL, route));
		if (params) {
			this.__getParams__(params, url.searchParams);
		}
		return await this.__execute__<R>(url.toString(), body, "PUT");
	}

	static async delete<R = any>(route: string, params?: Params): Promise<R> {
		const url = new URL(resolveUrl(API_URL, route));
		if (params) {
			this.__getParams__(params, url.searchParams);
		}
		return await this.__execute__<R>(url.toString(), undefined, "DELETE");
	}

	static async patch<R = any>(route: string, body?: RequestBody, params?: Params): Promise<R> {
		const url = new URL(resolveUrl(API_URL, route));
		if (params) {
			this.__getParams__(params, url.searchParams);
		}
		return await this.__execute__<R>(url.toString(), body, "PATCH");
	}

	static async options<R = any>(route: string, params?: Params): Promise<R> {
		const url = new URL(resolveUrl(API_URL, route));
		if (params) {
			this.__getParams__(params, url.searchParams);
		}
		return await this.__execute__<R>(url.toString(), undefined, "OPTIONS");
	}
}
